import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { loadTrackingSettings, injectMetaPixel, injectGoogleAnalytics } from '@/lib/tracking';
import { logCategoryOperation, sanitizeCategoryName } from '@/lib/categoryUtils';
import { updateMetaTags, updateFavicon, getCorretorMetaTags, resetMetaTags } from '@/utils/metaTags';
import { validateSession } from '@/lib/auth/simpleAuth';
import { loadGoogleFont, type StorefrontAppearance } from '@/lib/appearanceDefaults';
import { fetchPlatformThemeSettings, resolveStorefrontThemeId } from '@/lib/platformThemeSettings';
import { getOwnerPreviewTheme } from '@/lib/storefrontPreview';
import { getCachedAppearance, setCachedAppearance } from '@/lib/storefrontAppearanceCache';
import type { User } from '@/types';

interface UseCorretorDataProps {
  slug: string | undefined;
}

interface UseCorretorDataReturn {
  corretor: User | null;
  loading: boolean;
  error: string | null;
  preloadedAppearance: StorefrontAppearance | null;
}

/**
 * Custom hook for loading corretor data and applying theme/tracking
 */
export function useCorretorData({ slug }: UseCorretorDataProps): UseCorretorDataReturn {
  const [corretor, setCorretor] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [preloadedAppearance, setPreloadedAppearance] = useState<StorefrontAppearance | null>(null);

  useEffect(() => {
    // Validate session on component mount
    validateSession();
    
    if (slug) {
      fetchCorretorData();
    }

    return () => {
      try {
        // Reset meta tags to default when leaving the storefront
        resetMetaTags();
        // Clean up theme classes when leaving the storefront
        document.documentElement.classList.remove('light', 'dark');
      } catch (e) {
        console.error('Error cleaning up styles:', e);
      }
    };
  }, [slug]);

  const fetchCorretorData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!slug) {
        setError('Usuário não encontrado');
        return;
      }
      
      logCategoryOperation('LOADING_CORRETOR_DATA', { slug });

      // Load corretor data and tracking settings in parallel
      const [corretorResult, trackingResult] = await Promise.allSettled([
        supabase
          .from('users')
          .select(`
            id,
            name,
            email,
            role,
            phone,
            whatsapp,
            whatsapp_mode,
            whatsapp_link,
            country_code,
            avatar_url,
            social_icon_url,
            cover_url_desktop,
            cover_url_mobile,
            promotional_banner_url_desktop,
            promotional_banner_url_mobile,
            slug,
            is_blocked,
            bio,
            instagram,
            location_url,
            city,
            state,
            store_latitude,
            store_longitude,
            theme,
            active_storefront_theme_id,
            currency,
            language,
            plan_status,
            billing_cycle,
            subscription_end_date,
            referral_code,
            affiliate_program_enabled
          `)
          .eq('slug', slug)
          .eq('role', 'corretor')
          .maybeSingle(),
        loadTrackingSettings(slug) // We'll get the user ID from the first query
      ]);
      
      // Handle corretor data result
      if (corretorResult.status === 'rejected' || !corretorResult.value.data) {
        const error = corretorResult.status === 'rejected' ? corretorResult.reason : corretorResult.value.error;
        logCategoryOperation('CORRETOR_NOT_FOUND', { slug, error });
        setError(`Usuário não encontrado: ${slug}`);
        return;
      }
      
      const corretorData = corretorResult.value.data;
      
      logCategoryOperation('CORRETOR_LOADED', { 
        name: corretorData.name, 
        id: corretorData.id,
        slug: corretorData.slug,
        theme: corretorData.theme,
        currency: corretorData.currency,
        language: corretorData.language
      });
      
      setCorretor(corretorData);

      // Pre-fetch storefront appearance: try cache first, then fetch in background
      // Same fallback the page applies at render time, so we never preload the
      // hidden theme's appearance row into a store that will render "padrao".
      const activeThemeId = resolveStorefrontThemeId(
        corretorData.active_storefront_theme_id,
        await fetchPlatformThemeSettings(),
        corretorData.id,
        getOwnerPreviewTheme(corretorData.id)
      );
      const cachedAppearance = getCachedAppearance(corretorData.id, activeThemeId);
      if (cachedAppearance) {
        setPreloadedAppearance(cachedAppearance);
        loadGoogleFont(cachedAppearance.font_family);
        loadGoogleFont(cachedAppearance.heading_font_family);
      }

      supabase
        .from('storefront_appearance')
        .select('*')
        .eq('user_id', corretorData.id)
        .eq('theme_id', activeThemeId)
        .maybeSingle()
        .then(({ data }) => {
          if (data) {
            const appearance = data as StorefrontAppearance;
            setPreloadedAppearance(appearance);
            setCachedAppearance(corretorData.id, activeThemeId, appearance);
            loadGoogleFont(appearance.font_family);
            loadGoogleFont(appearance.heading_font_family);
          }
        });

      // Define current language with fallback
      const currentLanguage = corretorData.language || 'pt-BR';
      
      // Update meta tags for social media previews with correct title
      const metaConfig = getCorretorMetaTags(corretorData, currentLanguage);
      updateMetaTags(metaConfig);
      
      // Update favicon to the store's social icon (or avatar) if available
      if (corretorData.social_icon_url || corretorData.avatar_url) {
        updateFavicon(corretorData.social_icon_url || corretorData.avatar_url);
      }
      
      document.title = metaConfig.title;
      
      // Apply corretor's theme to the storefront
      if (corretorData.theme) {
        document.documentElement.classList.remove('light', 'dark');
        document.documentElement.classList.add(corretorData.theme);
      }

      // Load the per-store footer legal-links flag separately (fire-and-forget,
      // merged in once it resolves) rather than in the main select above: a
      // column this new can't be allowed to break the entire storefront load
      // for every store the moment it's referenced, only before its migration
      // has actually been applied. Missing/failed fetch just keeps the default
      // (links shown), same as before this flag existed.
      supabase
        .from('users')
        .select('show_footer_legal_links')
        .eq('id', corretorData.id)
        .maybeSingle()
        .then(({ data, error: footerLinksError }) => {
          if (!footerLinksError && data) {
            setCorretor(prev => prev ? { ...prev, show_footer_legal_links: data.show_footer_legal_links } : prev);
          }
        })
        .catch(() => {});

      // Load global Meta Pixel from site settings
      try {
        const { data: siteSettings, error: siteSettingsError } = await supabase
          .from('site_settings')
          .select('setting_value')
          .eq('setting_name', 'global_meta_pixel_id')
          .maybeSingle();

        if (!siteSettingsError && siteSettings?.setting_value) {
          console.log('Injecting global Meta Pixel:', siteSettings.setting_value);
          injectMetaPixel(siteSettings.setting_value);
        }
      } catch (globalPixelError) {
        console.warn('Error loading global Meta Pixel:', globalPixelError);
        // Don't fail the page load for global pixel errors
      }

      // Handle tracking settings result (non-blocking) - only for paid plans
      if (trackingResult.status === 'fulfilled' && corretorData.plan_status === 'active') {
        const trackingSettings = trackingResult.value;

        if (trackingSettings?.meta_pixel_id) {
          injectMetaPixel(trackingSettings.meta_pixel_id);
        }

        if (trackingSettings?.ga_measurement_id) {
          injectGoogleAnalytics(trackingSettings.ga_measurement_id);
        }
      } else if (trackingResult.status === 'rejected') {
        console.warn('Error loading tracking settings:', trackingResult.reason);
      }

    } catch (err: any) {
      logCategoryOperation('FETCH_CORRETOR_ERROR', err);
      console.error('Error fetching corretor data:', err);
      setError(`Error loading seller data: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  return {
    corretor,
    loading,
    error,
    preloadedAppearance
  };
}